import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Group,
  Text,
  Button,
  Paper,
  TextInput,
  Select,
  LoadingOverlay,
  Table,
  Avatar,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  PasswordInput,
  Checkbox,
  Pagination,
  Center,
} from "@mantine/core";
import {
  IconSearch,
  IconPlus,
  IconUser,
  IconEdit,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const BASE_URL = `${API_BASE_URL}/users`;
// =============================================================================
// CREATE USER MODAL
// =============================================================================
const CreateUserModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    nickname: "",
    password: "",
    role: "USER",
    isEmailVerified: false,
  });
  const { showSuccess, showError } = useNotification();

  const handleSubmit = async () => {
    if (!formData.email || !formData.password)
      return showError("Missing Data", "Email and Password are required");
    setLoading(true);

    try {
      const res = await fetch(BASE_URL, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      const data = await res.json();

      if (data.success) {
        showSuccess("Created", "User created successfully");
        onSuccess();
        onClose();
        setFormData({
          email: "",
          nickname: "",
          password: "",
          role: "USER",
          isEmailVerified: false,
        });
      } else {
        showError("Error", data.error || "Operation failed");
      }
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<Text fw={700}>Create New User</Text>}
    >
      <Stack>
        <LoadingOverlay visible={loading} />
        <TextInput
          label="Email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
        <TextInput
          label="Nickname"
          value={formData.nickname}
          onChange={(e) =>
            setFormData({ ...formData, nickname: e.target.value })
          }
        />
        <PasswordInput
          label="Password"
          required
          value={formData.password}
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
        />
        <Select
          label="Role"
          data={["USER", "SHOP_OWNER", "MODERATOR", "ADMIN", "SUPER_ADMIN"]}
          value={formData.role}
          onChange={(v) => setFormData({ ...formData, role: v })}
        />
        <Checkbox
          label="Mark Email as Verified"
          checked={formData.isEmailVerified}
          onChange={(e) =>
            setFormData({
              ...formData,
              isEmailVerified: e.currentTarget.checked,
            })
          }
        />
        <Button onClick={handleSubmit} fullWidth mt="md">
          Create Account
        </Button>
      </Stack>
    </Modal>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Pagination
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { showError } = useNotification();

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page,
        limit: 10,
        search,
        role: roleFilter === "ALL" ? "" : roleFilter,
      });

      const res = await fetch(`${BASE_URL}?${query}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();

      if (data.success) {
        setUsers(data.data);
        setPagination({
          page: data.pagination.page,
          totalPages: data.pagination.totalPages,
        });
      }
    } catch (e) {
      showError("Error", "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, [roleFilter]); // Auto-refresh on filter change, search needs manual enter or debounce ideally

  return (
    <Box className="space-y-6">
      {/* HEADER */}
      <Group justify="space-between">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            User Management
          </Text>
          <Text size="sm" c="dimmed">
            Manage system access and profiles
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={18} />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create User
        </Button>
      </Group>

      {/* FILTERS */}
      <Paper p="sm" radius="md" withBorder>
        <Group grow>
          <TextInput
            placeholder="Search email, nickname or ID..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchUsers(1)}
          />
          <Select
            placeholder="Filter by Role"
            data={[
              { label: "All Roles", value: "ALL" },
              "USER",
              "SHOP_OWNER",
              "ADMIN",
              "SUPER_ADMIN",
            ]}
            value={roleFilter}
            onChange={setRoleFilter}
          />
          <Button variant="light" onClick={() => fetchUsers(1)} w={100}>
            Search
          </Button>
        </Group>
      </Paper>

      {/* TABLE */}
      <Paper withBorder radius="md" overflow="hidden">
        <LoadingOverlay visible={loading} />
        <Table stickyHeader highlightOnHover verticalSpacing="sm">
          <Table.Thead bg="gray.1">
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Joined</Table.Th>
              <Table.Th align="right">Action</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.map((user) => (
              <Table.Tr key={user.cuid}>
                <Table.Td>
                  <Group gap="sm">
                    <Avatar src={user.profileImage} radius="xl" color="blue">
                      {user.nickname?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Text size="sm" fw={500}>
                        {user.nickname || "Unknown"}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {user.email}
                      </Text>
                    </Box>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge
                    variant="dot"
                    color={
                      user.role === "SUPER_ADMIN"
                        ? "red"
                        : user.role === "ADMIN"
                          ? "orange"
                          : user.role === "SHOP_OWNER"
                            ? "blue"
                            : "gray"
                    }
                  >
                    {user.role}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {user.isEmailVerified ? (
                    <Badge
                      variant="outline"
                      color="green"
                      leftSection={<IconCheck size={10} />}
                    >
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="outline" color="yellow">
                      Unverified
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </Text>
                </Table.Td>
                <Table.Td align="right">
                  <ActionIcon
                    variant="subtle"
                    onClick={() =>
                      navigate(`/admin/dashboard/users/${user.cuid}`)
                    }
                  >
                    <IconEdit size={18} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {users.length === 0 && !loading && (
          <Center p="xl">
            <Text c="dimmed">No users found.</Text>
          </Center>
        )}

        {/* Pagination */}
        <Center p="md" className="border-t border-gray-200">
          <Pagination
            total={pagination.totalPages}
            value={pagination.page}
            onChange={(p) => fetchUsers(p)}
          />
        </Center>
      </Paper>

      <CreateUserModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => fetchUsers(1)}
      />
    </Box>
  );
}
